import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {NgForm} from "@angular/forms";
import {interval, ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {ChronieJobListTypes} from "../../../shared/interface/chronie";
import {ChronieService} from "../../../shared/services/chronie.service";
import {AuthService} from "../../../shared/services/auth.service";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../shared/ngrx/reducers";
import {FilterService} from "../../../shared/services/filter.service";
import {ActivatedRoute, Router} from "@angular/router";
import {delay, distinctUntilChanged, takeUntil, tap} from "rxjs/operators";
import {getCurrentRouteState} from "../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../shared/ngrx/route-serializer";
import {Sort} from "@angular/material/sort";
import {CountedAndPaginated} from "../../../shared/interface/response/counted-and-paginated";
import {ClipboardService} from "../../../shared/services/clipboard.service";

@Component({
  selector: 'app-chronie-job-list',
  templateUrl: './chronie-job-list.component.html',
  styleUrls: ['./chronie-job-list.component.scss']
})
export class ChronieJobListComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() embeddedView = false;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;

  initialFilters: any;
  userPermissions: ReplaySubject<UserPermissions>;
  jobList: ChronieJobListTypes[] = [];
  displayedColumns: string[] = [
    'jobUuid',  'parameters', 'requestedAt', 'runTries', 'progress', 'output', 'error'
  ];
  totalData = 0;
  sortBy = 'requestedAt';
  sortOrder = 'DESC';


  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private chronieService: ChronieService,
    private auth: AuthService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private store: Store<StoreRootState>,
    private filterService: FilterService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private clipboard: ClipboardService
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.chronieService.chronieJobListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<ChronieJobListTypes>) => {
          this.jobList = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  ngOnInit(): void {
    interval(5000)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.paginator.pageIndex === 0) {
          this.loadListData();
        }
      });
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      policyUuid: 'string',
      orderUuid: 'string'
    }, filters);
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.sortBy = state.params?.sortBy ? state.params.sortBy : 'requestedAt';
        this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'DESC';
        this.loadListData();
      })
    ;
  }


  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;

    if (!this.embeddedView) {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        sortBy: $event.active,
        sortOrder: $event.direction,
        page: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value
      })]);
    } else {
      this.loadListData();
    }
  }

  buildFilters(): any {
    return {...this.initialFilters};
  }

  loadListData(): void {
    this.chronieService.getJobLst(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters(),
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      }
    );
  }

  copyMessage(val: string): void {
    this.clipboard.copy(val);
  }


  toEntries(obj: any): Array<{ key: string; value: string }> {
    if (!obj) { return []; }
    // Jeśli przychodzi jako string JSON – spróbuj sparsować
    if (typeof obj === 'string') {
      try {
        obj = JSON.parse(obj);
      } catch {
        return [{ key: 'value', value: obj }];
      }
    }
    return Object.keys(obj).map(k => ({
      key: this.prettyKey(k),
      value: this.prettyValue(k, obj[k]),
    }));
  }

  private prettyKey(key: string): string {
    // zamień camelCase/snake_case na czytelniejsze
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private prettyValue(key: string, val: any): string {
    if (val === null || val === undefined) { return ''; }
    const text = typeof val === 'object' ? JSON.stringify(val) : String(val);
    // proste maskowanie haseł/tokenów
    if (/password|secret|token/i.test(key)) {
      return '••••••••';
    }
    return text;
  }

}
